import React, { useState, useMemo } from 'react';
import { Button, Row, Col, FormGroup, Label, Input, Table } from 'reactstrap';
import DollarInput from '../DollarInput';
import { useUpdateSalesLocation } from '../../api/sales-locations/service';
import { toNumber, formatPriceWithDollar } from 'utils/currency';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

const accessoryToggleStyles = `
    .accessory-toggle .custom-toggle input:checked + .custom-toggle-slider:before {
        background-color: #ffffff !important;
    }
    .accessory-toggle .custom-toggle input + .custom-toggle-slider:before {
        background-color: #ffffff !important;
    }
`;

const getAccessoryDisplayName = (accessory) =>
    accessory?.display_name ?? accessory?.name ?? '';

/** API may return null for legacy rows — treat as active unless explicitly false */
const isAccessoryActive = (accessory) => accessory?.is_active !== false;

const toDollarFieldValue = (value) => {
    if (value === undefined || value === null || value === '') return '$0.00';
    if (typeof value === 'number') return formatPriceWithDollar(value);
    if (typeof value === 'string' && value.trim().startsWith('$')) return value;
    return formatPriceWithDollar(value);
};

const normalizeAccessoryForForm = (accessory = {}) => ({
    ...accessory,
    display_name: getAccessoryDisplayName(accessory),
    description: accessory.description ?? '',
    price: toDollarFieldValue(accessory.price),
    cost: toDollarFieldValue(accessory.cost),
    is_active: isAccessoryActive(accessory),
});

const buildAccessoryApiPayload = (accessory, extra = {}) => ({
    ...(accessory.id != null ? { id: accessory.id } : {}),
    ...(accessory.documentId ? { documentId: accessory.documentId } : {}),
    display_name: accessory.display_name ?? '',
    description: accessory.description ?? '',
    price: toNumber(accessory.price),
    cost: toNumber(accessory.cost),
    is_active: accessory.is_active !== false,
    ...extra,
});

const emptyAccessory = () => ({
    is_active: true,
    description: '',
    display_name: '',
    price: '$0.00',
    cost: '$0.00',
});

const Accessories = ({ formData, setFormData, activeColorStyle, isTheme2 = false, salesLocationId }) => {
    const safeFormData = formData || {};
    const accessories = safeFormData.accessories || [];
    const updateSalesLocationMutation = useUpdateSalesLocation();
    const inputStyle = useMemo(
        () => (isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined),
        [isTheme2]
    );
    const emptyStateStyle = useMemo(() => {
        const isLightTheme =
            typeof document !== 'undefined' &&
            document.body.classList.contains('white-content');
        return {
            textAlign: 'center',
            padding: '20px',
            color: isTheme2 && !isLightTheme ? 'rgba(255, 255, 255, 0.4)' : '#999',
        };
    }, [isTheme2]);

    const [showAccessoryForm, setShowAccessoryForm] = useState(false);
    const [currentAccessory, setCurrentAccessory] = useState(emptyAccessory);
    const [editingAccessoryIndex, setEditingAccessoryIndex] = useState(null);

    const persistAccessory = (apiAccessoryData) => {
        if (!salesLocationId) return;
        updateSalesLocationMutation.mutate({
            id: salesLocationId,
            data: { accessories: apiAccessoryData },
        });
    };

    const handleAddAccessory = () => {
        const apiAccessoryData = buildAccessoryApiPayload(currentAccessory);
        let updatedAccessories;

        if (editingAccessoryIndex !== null && showAccessoryForm) {
            updatedAccessories = [...accessories];
            updatedAccessories[editingAccessoryIndex] = {
                ...accessories[editingAccessoryIndex],
                ...currentAccessory,
            };
            setEditingAccessoryIndex(null);
        } else {
            updatedAccessories = [...accessories, { ...currentAccessory }];
        }

        setFormData((prev) => ({ ...prev, accessories: updatedAccessories }));
        persistAccessory(apiAccessoryData);

        setCurrentAccessory(emptyAccessory());
        setShowAccessoryForm(false);
    };

    const handleUpdateAccessory = () => {
        if (editingAccessoryIndex === null || !salesLocationId) return;

        const updatedAccessories = [...accessories];
        const mergedAccessory = {
            ...accessories[editingAccessoryIndex],
            ...currentAccessory,
        };
        updatedAccessories[editingAccessoryIndex] = mergedAccessory;
        setFormData((prev) => ({ ...prev, accessories: updatedAccessories }));
        persistAccessory(buildAccessoryApiPayload(mergedAccessory));
    };

    const handleEditAccessory = (index) => {
        if (editingAccessoryIndex === index) {
            setEditingAccessoryIndex(null);
            setCurrentAccessory(emptyAccessory());
            return;
        }

        setEditingAccessoryIndex(null);
        setShowAccessoryForm(false);

        setTimeout(() => {
            setCurrentAccessory(normalizeAccessoryForForm(accessories[index]));
            setEditingAccessoryIndex(index);
        }, 0);
    };

    const handleToggleAccessoryActive = (index, e) => {
        e?.stopPropagation?.();

        const updatedAccessories = [...accessories];
        const nextActive = !isAccessoryActive(updatedAccessories[index]);
        updatedAccessories[index] = {
            ...updatedAccessories[index],
            is_active: nextActive,
        };

        setFormData((prev) => ({ ...prev, accessories: updatedAccessories }));

        if (!nextActive && editingAccessoryIndex === index) {
            setEditingAccessoryIndex(null);
            setCurrentAccessory(emptyAccessory());
        }

        persistAccessory(buildAccessoryApiPayload(updatedAccessories[index]));
    };

    const handleCancelAccessory = () => {
        setCurrentAccessory(emptyAccessory());
        setEditingAccessoryIndex(null);
        setShowAccessoryForm(false);
    };

    return (
        <div className="accessory-toggle">
            <style>{accessoryToggleStyles}</style>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                {!showAccessoryForm && (
                    <Button
                        size="sm"
                        style={{ ...activeColorStyle, color: 'white' }}
                        onClick={() => {
                            setEditingAccessoryIndex(null);
                            setCurrentAccessory(emptyAccessory());
                            setShowAccessoryForm(true);
                        }}
                    >
                        Add Accessory
                    </Button>
                )}
            </div>

            {showAccessoryForm && (
                <>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Accessory Display Name:</Label>
                                <Input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle}
                                    value={currentAccessory.display_name}
                                    onChange={(e) =>
                                        setCurrentAccessory((prev) => ({
                                            ...prev,
                                            display_name: e.target.value,
                                        }))
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Description:</Label>
                                <Input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle}
                                    value={currentAccessory.description}
                                    onChange={(e) =>
                                        setCurrentAccessory((prev) => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Price:</Label>
                                <DollarInput
                                    className="form-control"
                                    style={inputStyle}
                                    value={currentAccessory.price}
                                    onChange={(value) =>
                                        setCurrentAccessory((prev) => ({ ...prev, price: value }))
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Cost:</Label>
                                <DollarInput
                                    className="form-control"
                                    style={inputStyle}
                                    value={currentAccessory.cost}
                                    onChange={(value) =>
                                        setCurrentAccessory((prev) => ({ ...prev, cost: value }))
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="text-right">
                            <Button
                                color="secondary"
                                size="sm"
                                onClick={handleCancelAccessory}
                                style={{ marginRight: '10px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                style={{ ...activeColorStyle, color: 'white' }}
                                onClick={handleAddAccessory}
                                disabled={updateSalesLocationMutation.isPending}
                            >
                                Add
                            </Button>
                        </Col>
                    </Row>
                </>
            )}

            {accessories.length === 0 ? (
                <div style={emptyStateStyle}>
                    No accessories added yet
                </div>
            ) : (
                <Table responsive>
                    <thead className="text-primary">
                        <tr>
                            <th>Active</th>
                            <th>Display Name</th>
                            <th>Description</th>
                            <th>Price</th>
                            <th>Cost</th>
                            <th className="text-right">Edit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accessories.map((accessory, idx) => {
                            const active = isAccessoryActive(accessory);
                            return (
                                <React.Fragment key={accessory.documentId || accessory.id || idx}>
                                    <tr
                                        onClick={() => {
                                            if (active) handleEditAccessory(idx);
                                        }}
                                    >
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <label className="custom-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={active}
                                                    onChange={(e) =>
                                                        handleToggleAccessoryActive(idx, e)
                                                    }
                                                />
                                                <span
                                                    className="custom-toggle-slider rounded-circle"
                                                    style={{
                                                        backgroundColor: active ? '#28a745' : '#6c757d',
                                                        borderColor: active ? '#28a745' : '#6c757d',
                                                    }}
                                                />
                                            </label>
                                        </td>
                                        <td>{getAccessoryDisplayName(accessory)}</td>
                                        <td>{accessory.description}</td>
                                        <td>{formatPriceWithDollar(accessory.price)}</td>
                                        <td>{formatPriceWithDollar(accessory.cost)}</td>
                                        <td className="text-right">
                                            <div className="actions-right">
                                                <Button
                                                    style={{ ...activeColorStyle, color: 'white' }}
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditAccessory(idx);
                                                    }}
                                                    disabled={!active}
                                                >
                                                    <i className="tim-icons icon-pencil" /> Edit
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    {editingAccessoryIndex === idx && (
                                        <tr>
                                            <td colSpan="6">
                                                <Row>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="form-control-label">
                                                                Accessory Display Name:
                                                            </Label>
                                                            <Input
                                                                type="text"
                                                                className="form-control"
                                                                style={inputStyle}
                                                                value={currentAccessory.display_name}
                                                                onChange={(e) =>
                                                                    setCurrentAccessory((prev) => ({
                                                                        ...prev,
                                                                        display_name: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="form-control-label">
                                                                Description:
                                                            </Label>
                                                            <Input
                                                                type="text"
                                                                className="form-control"
                                                                style={inputStyle}
                                                                value={currentAccessory.description}
                                                                onChange={(e) =>
                                                                    setCurrentAccessory((prev) => ({
                                                                        ...prev,
                                                                        description: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="form-control-label">Price:</Label>
                                                            <DollarInput
                                                                className="form-control"
                                                                style={inputStyle}
                                                                value={currentAccessory.price}
                                                                onChange={(value) =>
                                                                    setCurrentAccessory((prev) => ({
                                                                        ...prev,
                                                                        price: value,
                                                                    }))
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="form-control-label">Cost:</Label>
                                                            <DollarInput
                                                                className="form-control"
                                                                style={inputStyle}
                                                                value={currentAccessory.cost}
                                                                onChange={(value) =>
                                                                    setCurrentAccessory((prev) => ({
                                                                        ...prev,
                                                                        cost: value,
                                                                    }))
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col className="text-right">
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={handleCancelAccessory}
                                                            style={{ marginRight: '10px' }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            style={{ ...activeColorStyle, color: 'white' }}
                                                            onClick={handleUpdateAccessory}
                                                            disabled={updateSalesLocationMutation.isPending}
                                                        >
                                                            Update
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </div>
    );
};

export default Accessories;
